import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect } from 'react';
import { Transition } from 'react-spring';
import classnames from 'classnames';

// Assuming these imports are correct for your environment
import playIcon from './media/play.svg';
import style from './video-item.scss';

const VideoItem = (props) => {
    // Destructure all props
    const {
        videoTitle,
        showTitle,
        thumbnail,
        duration,
        isActive,
        handleClick,
        seasonInfo,
        isPassportVideo,
        settings: { displayVideoTitle, displayShowTitle, displayVideoDescription, displayVideoMeta }
    } = props;

    // State for tracking if the image has loaded
    const [imageLoaded, setImageLoaded] = useState(false);

    // Replacement for UNSAFE_componentWillUpdate:
    // This effect runs every time 'thumbnail' changes.
    // If the thumbnail changes, we reset imageLoaded to false.
    useEffect(() => {
        // If thumbnail is provided and changes, reset the loaded state
        if (thumbnail) {
            setImageLoaded(false);
        }
    }, [thumbnail]);

    // Handler to set state when the image finishes loading
    const onImageLoad = useCallback(() => {
        setImageLoaded(true);
    }, []);

    // Logic for shortDescription
    const shortDescription = displayVideoMeta
        ? seasonInfo?.length > 0
            ? [seasonInfo, props.shortDescription].join(' | ') + ' (' + duration + ')'
            : props.shortDescription + ' (' + duration + ')'
        : props.shortDescription;

    // Transition props remain the same
    const transitionProps = {
        component: false,
        config: {
            tension: 50,
            friction: 20
        },
        enter: {
            opacity: 1
        },
        leave: {
            opacity: 0
        }
    };

    return (
        <li
            className={classnames({
                active: isActive,
                'video-item': true,
                [style.videoItem]: true,
                'passport-video': isPassportVideo
            })}
            aria-current={isActive ? 'true' : undefined}>
            <button
                type="button"
                className="thumbnail-wrapper shape-placeholder-fill"
                onClick={handleClick}
                aria-label={`Play video: ${videoTitle || ''}${
                    showTitle ? ` from ${showTitle}` : ''
                }`}>
                {thumbnail && (
                    <span>
                        <img
                            src={thumbnail}
                            alt={videoTitle}
                            className={classnames({
                                'thumbnail-img': true,
                                loaded: imageLoaded
                            })}
                            onLoad={onImageLoad}
                            loading="lazy"
                        />
                        <img className="play-icon" src={playIcon} alt="play" />
                    </span>
                )}
            </button>
            <div className="video-info">
                <Transition {...transitionProps}>
                    {() =>
                        displayShowTitle && (
                            <h3 className="text-placeholder show-title" key="show-title">
                                {showTitle}
                            </h3>
                        )
                    }
                </Transition>
                <Transition {...transitionProps}>
                    {() =>
                        displayVideoTitle && (
                            <h4 className="text-placeholder video-title" key="video-title">
                                {videoTitle}
                            </h4>
                        )
                    }
                </Transition>
                <Transition {...transitionProps}>
                    {() =>
                        displayVideoDescription && (
                            <p
                                className="text-placeholder-long description short"
                                key="description">
                                {shortDescription}
                            </p>
                        )
                    }
                </Transition>
            </div>
        </li>
    );
};

VideoItem.propTypes = {
    componentId: PropTypes.string.isRequired,
    showTitle: PropTypes.string,
    videoTitle: PropTypes.string,
    duration: PropTypes.string,
    thumbnail: PropTypes.string,
    date: PropTypes.string,
    rating: PropTypes.string,
    shortDescription: PropTypes.string,
    seasonInfo: PropTypes.string,
    type: PropTypes.string,
    isActive: PropTypes.bool,
    handleClick: PropTypes.func.isRequired,
    settings: PropTypes.shape({
        displayVideoTitle: PropTypes.bool.isRequired,
        displayShowTitle: PropTypes.bool.isRequired,
        displayVideoDescription: PropTypes.bool.isRequired,
        displayVideoMeta: PropTypes.bool.isRequired
    }),
    isPassportVideo: PropTypes.bool
};

export default VideoItem;
